import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Disclosure } from '@components/disclosure';
import { useDeleteModal } from '@components/generic-modals';
import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconIntegrations } from '@components/icons/integrations';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRemove } from '@components/icons/remove';
import { IconSettings } from '@components/icons/settings';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useConnectModal } from './connect-modal';
import { useDisconnect, useToken } from './hooks';

export const JourneysSettingsPage = () => {
  const { t } = useScopedTranslate(locale);
  const tokenQuery = useToken();
  const data = tokenQuery.data;

  if (tokenQuery.isLoading || !data) {
    return <Spinner />;
  }
  const type = data.valid ? 'connected' : data.ok ? 'invalid' : 'notConnected';

  return (
    <Stack space={6}>
      {type === 'invalid' && (
        <Card space={6} variant="orange">
          <Stack space={2} orientation="horizontal" align="center">
            <IconFormIndicatorWarning />
            <Text font="regular14" color="orange200" text={t('invalidCredentialsWarning')} />
          </Stack>
        </Card>
      )}
      <Card space={6}>
        <Disclosure
          icon={<IconBrandJourneys />}
          iconLocation="inFront"
          text={t('journeys')}
          description={t('description')}
          content={[<JourneysDescription key={'journeyInfo'} />]}
        />
      </Card>
      {type === 'connected' && (
        <Card space={6} variant="gray">
          <Disclosure
            isDefaultOpen={false}
            icon={<IconSettings color="elementPurple50" />}
            text={t('settings')}
            description={t('noGlobalSettings')}
            isDisabled
            content={[]}
          />
        </Card>
      )}
    </Stack>
  );
};

const JourneysDescription = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <DescriptionContainer>
      <Stack space={6}>
        <Separator />
        <Text font="regular14" text={t('detailedDescriptin')} />
        <Separator />
        <Stack space={4}>
          <div>
            <Text font="bold14" text={t('withThisIntegration')} />
          </div>
          <Stack space={1} orientation="horizontal" align="center">
            <IconCheckmark color="blue100" />
            <Text font="regular14" text={t('addToJourneys')} />
          </Stack>
        </Stack>
        <Card spaceX={3} spaceY={4} variant="indigo-dark">
          <Stack orientation="horizontal" justify="space-between" align="center">
            <div>
              <Text font="bold14" text={t('pricing')} color="white100" />
              <Text font="regular14" text={t('free')} color="white100" />
            </div>
            <Text font="bold14" text="$0" color="white100" />
          </Stack>
        </Card>
        <Stack space={4}>
          <div>
            <Text font="bold14" text={t('builtBy')} />
            <Text font="regular14" text="50skills" />
          </div>
          <div>
            <Text font="bold14" text={t('website')} />
            <ActionText.ExternalLink
              href="https://50skills.com"
              font="regular14"
              color="purple100"
              underline
            >
              https://50skills.com
            </ActionText.ExternalLink>
          </div>
        </Stack>
      </Stack>
    </DescriptionContainer>
  );
};

export const JourneysActions = () => {
  const { t } = useScopedTranslate(locale);
  const showConnectModal = useConnectModal();
  const showDeleteModal = useDeleteModal();
  const disconnectMutation = useDisconnect();
  const tokenQuery = useToken();
  const data = tokenQuery.data;
  const type = data?.valid ? 'connected' : data?.ok ? 'invalid' : 'notConnected';
  return type === 'connected' ? (
    <div>
      <Stack space={4} orientation="horizontal" align="center">
        <TextStatusIndicator text={t('connected')} variant="success" />
        <Menu
          buttonProps={{
            variant: 'ghost',
            iconLeft: <IconMoreVertical />,
          }}
        >
          <Menu.Item
            icon={<IconRemove />}
            label={t('removeIntegration')}
            onSelect={() =>
              showDeleteModal({
                title: t('removeJourneys'),
                description: t('removeDescription'),
                buttonText: t('remove'),
                hasUserConfirmation: true,
                onConfirm: disconnectMutation.mutateAsync,
              })
            }
          />
        </Menu>
      </Stack>
    </div>
  ) : (
    <div>
      <Stack space={4} orientation="horizontal" align="center">
        {type === 'invalid' ? (
          <TextStatusIndicator text={t('invalidCredentials')} variant="pending" />
        ) : (
          <TextStatusIndicator text={t('notConnected')} variant="disabled" />
        )}
        <Button
          variant="primary"
          text={t('connect')}
          iconLeft={<IconIntegrations />}
          onClick={showConnectModal}
        />
      </Stack>
    </div>
  );
};

const DescriptionContainer = styled.div({ marginTop: pxToRem(24) });

const locale = {
  en: {
    journeys: 'Journeys',
    description: 'Add applicants to existing journeys',
    detailedDescriptin:
      'The people process automation platform for seamless employee journeys and enhanced productivity.\n\nTake control of your people processes with an easy to use, no-code employee journey builder, that increases productivity, performance & retention.',
    withThisIntegration: 'With this integration, you can',
    addToJourneys: 'Add applicants to existing journeys',
    pricing: 'Pricing',
    free: 'Free',
    builtBy: 'Built by',
    website: 'Website',
    settings: 'Settings',
    noGlobalSettings: 'There are no global settings for this integration',
    invalidCredentialsWarning:
      'Your credentials have either expired or been removed, please reconnect to re-enable this integration',
    removeJourneys: 'Remove Journeys?',
    removeDescription:
      'Are you sure you want to remove Journeys?\nIntegration functionality will no longer be available',
    connect: 'Connect',
    connected: 'Connected',
    notConnected: 'Not connected',
    invalidCredentials: 'Invalid credentials',
    removeIntegration: 'Remove integration',
    remove: 'Remove',
  },
  is: {
    journeys: 'Journeys',
    description: 'Bættu við umsækjendum í journey hjá þínu fyrirtæki',
    detailedDescriptin:
      'Útfærðu og sjálfvirknivæddu mannauðsferla þíns fyrirtækis.\n\nTaktu stjórn á mannauðsferlum þíns fyrirtækis með einföldum og þægilegum hætti.',
    withThisIntegration: 'Með þessari tengingu færðu möguleikann',
    addToJourneys: 'Senda umsækjendur í fyrirfram tilbúin journey',
    pricing: 'Verð',
    free: 'Frítt',
    builtBy: 'Útfært af',
    website: 'Vefsíða',
    settings: 'Stillingar',
    noGlobalSettings: 'Engar stillingar eru til taks fyrir þessa tengingu',
    invalidCredentialsWarning:
      'Þínar aðgangssupplýsingar eru útrunnar eða hafa verið fjarlægðar, vinsamlegast setjið upp tenginguna aftur',
    removeJourneys: 'Fjarlægja Journeys?',
    removeDescription:
      'Ertu viss um að þú viljir fjarlægja Journeys tenginguna?\nÖll virkni og gögn tengd henni verða ekki aðgengileg notendum.',
    connect: 'Tengja',
    connected: 'Tengt',
    notConnected: 'Ótengt',
    invalidCredentials: 'Ógildar aðgangsupplýsingar',
    removeIntegration: 'Fjarlægja tengingu',
    remove: 'Fjarlægja',
  },
};
